import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useViewer } from '@/entities/viewer';
import { LocaleSwitcher } from '@/features/locale-switcher';
import { useMediaQuery } from '@/shared/lib/media';
import { Icons, Popover } from '@/shared/ui';

import styles from './LandingHeader.module.css';
import { Logo } from './Logo';
import { SECTION_IDS } from './sections';

/** Below this width the section links move into one menu (the composition changes, not just size). */
const COMPACT_QUERY = '(max-width: 860px)';

/**
 * The public header: brand, links to the sections of this page, the language and the account
 * actions. Anonymous visitors get "Sign in" and "Get started"; a signed-in one gets one button
 * into the application (never a registration prompt).
 */
export function LandingHeader() {
  const { t } = useTranslation();
  const viewer = useViewer();
  const compact = useMediaQuery(COMPACT_QUERY);
  const signedIn = viewer.status === 'authenticated';
  const primary = signedIn ? (
    <Link to="/app" className={styles.primary}>
      {t('landing.cta.open')}
    </Link>
  ) : (
    <Link to="/register" className={styles.primary}>
      {t('landing.cta.start')}
    </Link>
  );

  return (
    <header className={styles.header}>
      <div className={styles.bar}>
        <a href="#top" className={styles.brand} aria-label={t('app.name')}>
          <Logo />
          <span className={styles.brandName}>{t('app.name')}</span>
        </a>
        {!compact && (
          <nav className={styles.nav} aria-label={t('landing.nav.label')}>
            {SECTION_IDS.map((id) => (
              <a key={id} href={`#${id}`}>
                {t(`landing.nav.${id}`)}
              </a>
            ))}
          </nav>
        )}
        <div className={styles.actions}>
          {!compact && <LocaleSwitcher />}
          {!compact && !signedIn && (
            <Link to="/login" className={styles.link}>
              {t('landing.cta.signIn')}
            </Link>
          )}
          {primary}
          {compact && (
            <Popover
              label={t('landing.nav.menu')}
              width={260}
              alignEnd
              trigger={(props) => (
                <button
                  type="button"
                  {...props}
                  className={styles.menuButton}
                  aria-label={t('landing.nav.menu')}
                  data-testid="landing-menu-button"
                >
                  <Icons.menu />
                </button>
              )}
            >
              {(close) => (
                <nav aria-label={t('landing.nav.label')} className={styles.menu}>
                  {SECTION_IDS.map((id) => (
                    <a key={id} href={`#${id}`} className={styles.menuItem} onClick={close}>
                      {t(`landing.nav.${id}`)}
                    </a>
                  ))}
                  {!signedIn && (
                    <Link to="/login" className={styles.menuItem} onClick={close}>
                      {t('landing.cta.signIn')}
                    </Link>
                  )}
                  <div className={styles.menuLocale}>
                    <LocaleSwitcher />
                  </div>
                </nav>
              )}
            </Popover>
          )}
        </div>
      </div>
    </header>
  );
}
